/**
 * WorkspacePlanChip — iter-170an follow-up.
 *
 * Tiny chip in the sidebar showing the ACTIVE workspace's plan.
 * Visible everywhere (workspaces switcher slot in the sidebar) so
 * the user always knows whether they're on Free or a paid plan, and
 * has a one-click path to /pricing to upgrade.
 *
 * Colors:
 *   • Free / no plan   → slate (neutral, no shame, just info)
 *   • Paid plan        → violet (matches /pricing primary accent)
 *
 * Two render modes:
 *   • full — pill with plan name (e.g. "Free", "Pro", "Studio").
 *   • rail — just the colored dot (icon-only sidebar).
 *
 * Click → navigates to /pricing. The Pricing page's selector
 * defaults to the active workspace, so the upgrade flow lands
 * pre-scoped in zero clicks.
 */
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useLang } from "../contexts/LangContext";
import { useSpace } from "../contexts/SpaceContext";
import { api } from "../lib/api";
import { cn } from "../lib/utils";
import { tx } from "../i18n";

export function WorkspacePlanChip({ rail = false }) {
  const { lang } = useLang();
  const { activeSpaceId } = useSpace();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ["composition-me", activeSpaceId],
    queryFn: async () => (await api.get("/billing/composition/me")).data,
    enabled: !!activeSpaceId,
    staleTime: 60_000,
    retry: false,
  });

  if (isLoading) {
    return (
      <div
        className={cn(
          "rounded-full bg-slate-100 animate-pulse",
          rail ? "h-2.5 w-2.5 mx-auto" : "h-5 w-12",
        )}
        data-testid="workspace-plan-chip-skeleton"
      />
    );
  }

  const planName = data?.plan?.name || null;
  const isPaid = !!planName;
  const label = planName || tx(lang, "Free", "Free", "Free");

  const tone = isPaid
    ? "bg-sky-100 text-sky-700 hover:bg-sky-200"
    : "bg-slate-100 text-slate-600 hover:bg-slate-200";

  // iter-170an follow-up — Use a ``span[role="button"]`` instead of a
  // native ``<button /* design-system: ALLOW legacy-todo */>``. The chip is embedded inside the
  // SpaceSwitcher's DropdownMenuTrigger which is itself a ``<button /* design-system: ALLOW legacy-todo */>``;
  // a nested-button tree throws a React hydration warning AND breaks
  // accessibility. We re-implement focus/keyboard support manually so
  // the chip is still tab-reachable and Enter-activatable.
  const stop = (e) => {
    e.stopPropagation();
    e.preventDefault();
  };
  const go = (e) => {
    e.stopPropagation();
    e.preventDefault();
    navigate("/pricing");
  };
  const onKey = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.stopPropagation();
      e.preventDefault();
      navigate("/pricing");
    }
  };

  if (rail) {
    return (
      <span
        role="button"
        tabIndex={0}
        onClick={go}
        onKeyDown={onKey}
        onPointerDown={stop}
        onMouseDown={stop}
        data-testid="workspace-plan-chip-rail"
        title={`${tx(lang, "Plan", "Plan", "Plano")}: ${label}`}
        className="block mx-auto cursor-pointer outline-none focus:ring-2 focus:ring-sky-300 rounded-full"
      >
        <span
          className={cn(
            "block h-2.5 w-2.5 rounded-full transition-colors",
            isPaid ? "bg-sky-500" : "bg-slate-400",
          )}
        />
      </span>
    );
  }

  return (
    <span
      role="button"
      tabIndex={0}
      onClick={go}
      onKeyDown={onKey}
      onPointerDown={stop}
      onMouseDown={stop}
      data-testid="workspace-plan-chip"
      title={`${tx(lang, "Current plan", "Plan actual", "Plano atual")}: ${label}`}
      className={cn(
        "inline-flex items-center justify-center px-2 py-0.5 rounded-full",
        "text-[10px] font-bold uppercase tracking-wider transition-colors cursor-pointer",
        "outline-none focus:ring-2 focus:ring-sky-300",
        tone,
      )}
    >
      {label}
    </span>
  );
}
